/** A date range of ISO `YYYY-MM-DD` days, both ends inclusive. `null` = open end. */
export type NxDateRange = { from: string | null; to: string | null };

export type NxDateRangePreset = {
  id: string;
  label: string;
  /** The range this preset stands for, relative to `today` (ISO `YYYY-MM-DD`). */
  range: (today: string) => NxDateRange;
};

function parse(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function format(date: Date): string {
  const y = String(date.getFullYear()).padStart(4, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Today as ISO `YYYY-MM-DD` in local time. */
export function todayIso(now: Date = new Date()): string {
  return format(now);
}

export function addDays(iso: string, days: number): string {
  const date = parse(iso);
  date.setDate(date.getDate() + days);
  return format(date);
}

/** Monday of the ISO week `iso` lies in. */
export function startOfWeek(iso: string): string {
  const weekday = (parse(iso).getDay() + 6) % 7; // Monday = 0
  return addDays(iso, -weekday);
}

/**
 * `weeks` whole weeks (Monday to Sunday), starting with the week `offset`
 * weeks from the one `today` lies in (0 = this week, 1 = next week).
 */
export function weekRange(today: string, weeks = 1, offset = 0): NxDateRange {
  const from = addDays(startOfWeek(today), offset * 7);
  return { from, to: addDays(from, weeks * 7 - 1) };
}

export function monthRange(today: string): NxDateRange {
  const date = parse(today);
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  const last = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { from: format(first), to: format(last) };
}

export type NxDateRangePresetLabels = {
  today: string;
  thisWeek: string;
  nextWeek: string;
  twoWeeks: string;
  thisMonth: string;
};

const defaultLabels: NxDateRangePresetLabels = {
  today: 'Heute',
  thisWeek: 'Diese Woche',
  nextWeek: 'Nächste Woche',
  twoWeeks: '2 Wochen',
  thisMonth: 'Dieser Monat',
};

/**
 * The planning presets of the work-organisation screens. Pass other labels
 * for another language; build an own list for other ranges.
 *
 * Not the dashboard's `timeRange.ts`: that one produces RELATIVE tokens
 * („-7d“) the server resolves at query time. A picker needs concrete days the
 * user can see and adjust, so these resolve to dates on the client.
 */
export function defaultDateRangePresets(
  labels: Partial<NxDateRangePresetLabels> = {}
): NxDateRangePreset[] {
  const l = { ...defaultLabels, ...labels };
  return [
    { id: 'today', label: l.today, range: (today) => ({ from: today, to: today }) },
    { id: 'this-week', label: l.thisWeek, range: (today) => weekRange(today, 1, 0) },
    { id: 'next-week', label: l.nextWeek, range: (today) => weekRange(today, 1, 1) },
    { id: 'two-weeks', label: l.twoWeeks, range: (today) => weekRange(today, 2, 0) },
    { id: 'this-month', label: l.thisMonth, range: monthRange },
  ];
}

export function sameRange(a: NxDateRange, b: NxDateRange): boolean {
  return a.from === b.from && a.to === b.to;
}
